import { Link } from "react-router-dom";
import {
  ArrowRight,
  Bot,
  BookOpen,
  Mail,
  ShieldCheck,
  Sparkles,
  AtSign,
  Cpu,
  Check,
  Plug,
  HelpCircle,
  ArrowDown,
  Image as ImageIcon,
  Monitor,
  Target,
  FileSearch,
  MessageCircleQuestion,
  Network,
  RotateCcw,
} from "lucide-react";

export default function Landing() {
  return (
    <div className="relative min-h-full bg-slate-50 text-slate-800 font-sans overflow-x-hidden selection:bg-brand-500 selection:text-white">
      {/* Decorative background glows */}
      <div className="absolute top-[-100px] left-[50%] -translate-x-1/2 w-[1000px] h-[400px] rounded-full bg-radial from-brand-200/40 via-brand-50/10 to-transparent blur-3xl -z-10 pointer-events-none" />
      <div className="absolute top-[800px] left-[-200px] w-[600px] h-[600px] rounded-full bg-radial from-indigo-100/30 to-transparent blur-3xl -z-10 pointer-events-none" />
      
      <Nav />
      <Hero />
      <WhoItsFor />
      <HowItWorks />
      <AgentDecisions />
      <WhyTriage />
      <Workflows />
      <Pricing />
      <Footer />
    </div>
  );
}

/* ── Nav ──────────────────────────────────────────────────────────────── */

function Nav() {
  return (
    <header className="sticky top-0 z-40 w-full border-b border-slate-200/50 bg-white/70 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-linear-to-tr from-brand-600 to-brand-500 text-white shadow-sm">
            <Sparkles className="h-4.5 w-4.5" />
          </div>
          <span className="text-base font-extrabold tracking-tight text-slate-905">Triage</span>
        </div>
        <nav className="hidden items-center gap-7 text-sm font-semibold text-slate-500 md:flex">
          <a href="#how" className="hover:text-brand-600 transition-colors">How it works</a>
          <a href="#agent" className="hover:text-brand-600 transition-colors">Agent</a>
          <a href="#features" className="hover:text-brand-600 transition-colors">Features</a>
          <a href="#workflows" className="hover:text-brand-600 transition-colors">Workflows</a>
          <a href="#pricing" className="hover:text-brand-600 transition-colors">Pricing</a>
        </nav>
        <div className="flex items-center gap-4">
          <Link to="/signin" className="text-sm font-semibold text-slate-650 hover:text-slate-900 transition-colors">
            Sign in
          </Link>
          <Link
            to="/signup"
            className="inline-flex items-center gap-1.5 rounded-xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 shadow-sm transition-all duration-200 hover:shadow active:scale-[0.98]"
          >
            Start free <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </header>
  );
}

/* ── Hero ─────────────────────────────────────────────────────────────── */

function Hero() {
  return (
    <section className="relative pt-20 pb-16 md:pt-28 md:pb-24">
      <div className="mx-auto max-w-6xl px-6">
        <div className="mx-auto max-w-4xl text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-200/60 bg-brand-50/50 px-3.5 py-1.5 text-xs font-semibold text-brand-700 animate-fade-in shadow-2xs">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" /> Now in early access
          </span>
          <h1 className="mt-8 text-4xl font-extrabold tracking-tight text-slate-950 sm:text-5xl md:text-6xl leading-tight">
            Forward your support email.{" "}
            <span className="bg-gradient-to-r from-brand-600 to-indigo-600 bg-clip-text text-transparent">
              AI answers it.
            </span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-slate-500">
            For SaaS founders, Shopify store owners, and small service teams drowning in the
            same 10 questions a day. Forward <code className="font-mono bg-slate-100 rounded px-1 text-sm font-semibold text-slate-800">support@</code>
            {" "}to Triage and AI takes the routine email off your plate — categorize, ask the
            customer for anything missing, draft a grounded reply, send. You stay in control.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 rounded-xl bg-slate-950 px-6 py-3.5 text-sm font-bold text-white hover:bg-slate-800 shadow-md shadow-slate-950/10 transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.98]"
            >
              Start free <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="#how"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-6 py-3.5 text-sm font-bold text-slate-700 hover:bg-slate-50 shadow-xs transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.98]"
            >
              See how it works
            </a>
          </div>
          <p className="mt-4 text-xs font-semibold text-slate-400">
            15-day free trial · 100 emails included · no credit card
          </p>
        </div>
      </div>
    </section>
  );
}

/* ── Who it's for ─────────────────────────────────────────────────────── */

function WhoItsFor() {
  const personas = [
    {
      icon: Sparkles,
      title: "SaaS founders & small teams",
      who: "1–5 people. 50–500 support emails a month. No dedicated CS person.",
      pain: "You're answering the same 10 questions instead of building.",
      win: "Reclaim hours per week — AI handles routine email, you handle the hard stuff.",
      iconBg: "bg-brand-50 text-brand-600 border-brand-100/50"
    },
    {
      icon: Mail,
      title: "Shopify & SMB e-commerce",
      who: "200–2,000 orders a month. Gmail or shared inbox, no helpdesk.",
      pain: "\"Where's my order?\" and \"How do I return this?\" never stop.",
      win: "Reply instantly with order context. Cheaper than chat bots, better at email.",
      iconBg: "bg-indigo-50 text-indigo-650 border-indigo-100/50"
    },
    {
      icon: BookOpen,
      title: "Service businesses & creators",
      who: "Agencies, consultants, course creators, coaches. IT teams.",
      pain: "Appointment, billing, and access questions eat your day.",
      win: "Knowledge-grounded replies in your voice — customers can't tell the difference.",
      iconBg: "bg-violet-50 text-violet-650 border-violet-100/50"
    },
  ];

  return (
    <section className="py-20 border-t border-slate-200/60 bg-white">
      <div className="mx-auto max-w-6xl px-6">
        <div className="max-w-3xl">
          <h2 className="text-3xl font-extrabold tracking-tight text-slate-950 sm:text-4xl">
            Built for teams drowning in support emails
          </h2>
          <p className="mt-4 text-base text-slate-500 leading-relaxed">
            We're not for enterprise CX departments on Zendesk. We're for the teams who keep
            promising to "set up proper support next quarter."
          </p>
        </div>
        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
          {personas.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="group rounded-2xl border border-slate-200/60 bg-slate-50/40 p-7 hover:bg-white hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <div className={`flex h-11 w-11 items-center justify-center rounded-xl border ${p.iconBg}`}>
                  <Icon className="h-5.5 w-5.5" />
                </div>
                <h3 className="mt-5 text-lg font-bold text-slate-900">{p.title}</h3>
                <p className="mt-1.5 text-xs font-semibold text-slate-400">{p.who}</p>
                <div className="mt-5 space-y-2 text-sm">
                  <p className="text-slate-650">
                    <span className="font-bold text-slate-900">Pain:</span> {p.pain}
                  </p>
                  <p className="text-slate-650">
                    <span className="font-bold text-emerald-600">With Triage:</span> {p.win}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ── How it works ─────────────────────────────────────────────────────── */

function HowItWorks() {
  const steps = [
    {
      n: "01",
      title: "Forward support@ to Triage",
      body: "Get a dedicated inbound address in under five minutes, then point your existing support@yourcompany.com at it via Gmail / Workspace / Cloudflare. Your customers email the same address they always did.",
    },
    {
      n: "02",
      title: "Tell the AI what to handle",
      body: "Pick a starter pack (password reset, billing, order status) or define your own categories. Paste your FAQs and policies — the AI uses them as ground truth.",
    },
    {
      n: "03",
      title: "AI handles routine, you handle the rest",
      body: "Triage classifies, asks the customer for missing info, drafts the reply, and either sends automatically (high-confidence) or queues for review. You stay in control.",
    },
  ];
  return (
    <section id="how" className="py-20 border-t border-slate-200/60 bg-slate-50/50">
      <div className="mx-auto max-w-6xl px-6">
        <div className="max-w-3xl">
          <h2 className="text-3xl font-extrabold tracking-tight text-slate-950 sm:text-4xl">
            How it works
          </h2>
          <p className="mt-4 text-base text-slate-500">
            Three simple steps from support overwhelm to automation.
          </p>
        </div>
        <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-3">
          {steps.map((s) => (
            <div key={s.n} className="relative rounded-2xl border border-slate-200 bg-white p-7 shadow-2xs">
              <span className="font-mono text-sm font-bold text-brand-600 bg-brand-50 px-2.5 py-1 rounded-lg border border-brand-100">{s.n}</span>
              <h3 className="mt-5 text-lg font-bold tracking-tight text-slate-900">{s.title}</h3>
              <p className="mt-2.5 text-sm text-slate-500 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Agent decisions ──────────────────────────────────────────────────── */

function AgentDecisions() {
  const decisions = [
    {
      icon: Target,
      title: "Classifies intent",
      body: "Bedrock picks the right category from the intents you've defined. Deterministic rules first; LLM fallback for the long tail.",
    },
    {
      icon: FileSearch,
      title: "Extracts slot values",
      body: "Two-pass extraction (regex + LLM tool-use) pulls slot values like order IDs, account emails, and error codes directly from the body.",
    },
    {
      icon: MessageCircleQuestion,
      title: "Decides ask vs. answer",
      body: "If a required slot is missing, the AI composes a polite follow-up email instead of inventing answers. Multi-turn close loop.",
    },
    {
      icon: Network,
      title: "Calls your APIs",
      body: "Connectors fetch live data — order status, account state — from your existing endpoints. The reply quotes real values, not guesses.",
    },
    {
      icon: ImageIcon,
      title: "Reads screenshots",
      body: "Multi-modal Bedrock interprets pasted images and attachments. Quotes exact error codes and UI state back to the customer.",
    },
    {
      icon: RotateCcw,
      title: "Self-corrects",
      body: "Reclassify any case with one click — the pipeline re-runs with the updated category and knowledge bank. No human-in-the-loop friction.",
    },
  ];

  return (
    <section id="agent" className="py-20 border-t border-slate-200/60 bg-white">
      <div className="mx-auto max-w-6xl px-6">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-250 bg-slate-50 px-3.5 py-1.5 text-xs font-semibold text-slate-600">
            <Sparkles className="h-3 w-3" /> Agent design
          </span>
          <h2 className="mt-6 text-3xl font-extrabold tracking-tight text-slate-950 sm:text-4xl">
            Agentic decisions. Predictable behavior.
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-500">
            Triage makes multiple bounded AI decisions per ticket — each grounded in your data,
            auditable, and predictable in cost. Open-loop agents are exciting in demos, but brutal at production scale.
          </p>
        </div>
        <div className="mt-16 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {decisions.map((d) => {
            const Icon = d.icon;
            return (
              <div key={d.title} className="rounded-2xl border border-slate-100 bg-slate-50/30 p-6 hover:shadow-xs transition-shadow">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-950 text-white shadow-xs">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 text-base font-bold text-slate-900">{d.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">{d.body}</p>
              </div>
            );
          })}
        </div>
        <div className="mx-auto mt-16 max-w-3xl rounded-2xl border border-brand-100 bg-linear-to-r from-brand-50/50 to-indigo-50/30 p-6">
          <p className="text-sm leading-relaxed text-slate-650">
            <strong className="text-slate-900">Open-loop ReAct agents run 5–20 LLM calls per ticket. Triage runs 2–4.</strong>{" "}
            The bounded shape isn't a limitation — it's why this costs just a fraction per ticket
            and why you can actually offer an SLA, and why your monthly bill doesn't
            spike when someone forwards a long thread.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ── Why Triage ────────────────────────────────────────────────────────── */

function WhyTriage() {
  const edges = [
    {
      icon: Mail,
      title: "No helpdesk required",
      body: "Forward your support@ to Triage and you're live in 10 minutes. No migration project, no Zendesk platform lock-in.",
    },
    {
      icon: HelpCircle,
      title: "Handles multi-turn conversations",
      body: "Triage asks the customer for missing slots (order ID, etc.), then writes the real reply after they answer. Slots + Gather closes the loop.",
    },
    {
      icon: ImageIcon,
      title: "Reads screenshots",
      body: "Multi-modal Bedrock reads screenshots and attachments to grab the exact error messages or invoice details. No guessing.",
    },
    {
      icon: BookOpen,
      title: "Grounded in your knowledge",
      body: "Paste your FAQs and policies. The AI treats them as authoritative and refuses to invent prices or refund terms.",
    },
    {
      icon: Cpu,
      title: "Bring your own AI model",
      body: "Runs on AWS Bedrock — pick Claude, Nova, or Cohere. We don't mark up token costs 5–20× like other services.",
    },
    {
      icon: ShieldCheck,
      title: "Safety guardrails by default",
      body: "Content filters catch abuse. Dry-run mode lets you test drafts before any email actually gets sent to customers.",
    },
    {
      icon: AtSign,
      title: "Native sender + threading",
      body: "Verify your support@ domain so replies thread back. Customer replies loop back automatically into the conversation.",
    },
  ];
  return (
    <section id="features" className="py-20 border-t border-slate-200/60 bg-slate-50/50">
      <div className="mx-auto max-w-6xl px-6">
        <div className="max-w-3xl">
          <h2 className="text-3xl font-extrabold tracking-tight text-slate-950 sm:text-4xl">
            Why Triage
          </h2>
          <p className="mt-4 text-base text-slate-500">
            Built for email-first support operations. You stay in complete control of cost and replies.
          </p>
        </div>
        <div className="mt-14 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {edges.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-2xs">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-700">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-base font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-550">{f.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ── Workflows ─────────────────────────────────────────────────────────── */

function Workflows() {
  return (
    <section id="workflows" className="py-20 border-t border-slate-200/60 bg-slate-950 text-slate-100">
      <div className="mx-auto max-w-6xl px-6">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-800 bg-slate-900 px-3.5 py-1.5 text-xs font-semibold text-slate-400">
            <Sparkles className="h-3 w-3" /> See it in action
          </span>
          <h2 className="mt-6 text-3xl font-extrabold tracking-tight sm:text-4xl">
            The AI doesn't just draft. It has a conversation.
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-450">
            When an email is missing info to answer, Triage politely asks for it, then writes the real reply when they respond.
          </p>
        </div>

        {/* Pipeline row */}
        <div className="mt-16 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Step n="01" icon={Bot} title="Understand" body="Classify email into categories using Bedrock LLM + examples." />
          <Step n="02" icon={HelpCircle} title="Gather" body="Ask customers for missing slots (order ID, phone, etc.)." />
          <Step n="03" icon={Mail} title="Respond" body="Draft grounded reply. Send auto or queue for review." />
          <Step n="04" icon={ShieldCheck} title="Escalate" body="Unresolved cases forward to human inbox with context." />
        </div>

        {/* Conversation example */}
        <div className="mt-20 grid grid-cols-1 items-start gap-12 lg:grid-cols-2">
          <div>
            <h3 className="text-xl font-bold tracking-tight text-white">
              Login-issue flow with a screenshot
            </h3>
            <p className="mt-2.5 text-sm text-slate-400 leading-normal">
              Customer sends a screenshot. Triage reads it, identifies the error code, and drafts a resolution based on the knowledge bank.
            </p>

            <div className="mt-8 space-y-4">
              <ChatBubble side="customer" who="priya@example.com" time="11:42">
                Hi — can't log in this morning. Screenshot below.
                <ScreenshotStub label="login_error.png" caption="Login page · 'Error: TOKEN_EXPIRED — your session refresh failed. Please sign in again.'" />
              </ChatBubble>

              <SystemNote>
                Classified <strong className="text-white">Login issues</strong> · 94% · <strong className="text-white">screenshot extracted</strong>: error <code>TOKEN_EXPIRED</code> · slot needed: <code>account_email</code>
              </SystemNote>

              <ChatBubble side="ai" who="Triage" time="11:42">
                Hi Priya — thanks for the screenshot. That's a TOKEN_EXPIRED error, which means your session expired. Could you confirm the email address you sign in with so I can send a fresh login link?
              </ChatBubble>

              <ChatBubble side="customer" who="priya@example.com" time="11:48">
                priya.k@example.com
              </ChatBubble>

              <SystemNote>
                Slots filled · drafting reply grounded in knowledge bank
              </SystemNote>

              <ChatBubble side="ai" who="Triage" time="11:48">
                Thanks! I've sent a login link to <strong>priya.k@example.com</strong>. Clear cookies, verify system clock skew, and disable Cross-Site tracking if issues persist. Reply if you need help!
              </ChatBubble>
            </div>
          </div>

          {/* Config Preview */}
          <div className="lg:sticky lg:top-24 space-y-4">
            <h3 className="text-xl font-bold tracking-tight text-white">
              Configure a category once
            </h3>
            <p className="text-sm text-slate-400">
              Provide examples to train the classifier, and define slots the AI must collect.
            </p>

            <div className="rounded-2xl border border-slate-800 bg-slate-950 p-6 shadow-2xl">
              <div className="mb-4 flex items-center gap-1.5">
                <span className="h-3 w-3 rounded-full bg-rose-500/80" />
                <span className="h-3 w-3 rounded-full bg-amber-500/80" />
                <span className="h-3 w-3 rounded-full bg-emerald-500/80" />
                <span className="ml-3 font-semibold text-xs text-slate-500">Password reset config</span>
              </div>
              <pre className="overflow-x-auto font-mono text-[11px] leading-relaxed text-slate-350">
                <code>
{`{
  "category": "Password reset",
  "slots": [
    { "name": "account_email", "required": true }
  ],
  "knowledgeBank": "Reset links valid 24h. Check spam...",
  "autoSendThreshold": 0.85,
  "replyMode": "direct"
}`}
                </code>
              </pre>
            </div>

            <ul className="space-y-3.5 text-sm text-slate-400">
              <li className="flex items-start gap-2.5">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                <span>Examples train the classifier — accuracy improves as you label more.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                <span>Knowledge bank is authoritative — the AI won't invent policies.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <Plug className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                <span className="text-amber-250/90">Connectors (call billing/order APIs) — <span className="font-semibold">coming soon</span>.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function Step({
  n,
  icon: Icon,
  title,
  body,
}: {
  n: string;
  icon: typeof Bot;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6">
      <div className="flex items-center justify-between">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-800 text-slate-200">
          <Icon className="h-4 w-4" />
        </div>
        <span className="font-mono text-xs font-bold text-slate-600">{n}</span>
      </div>
      <p className="mt-4 text-base font-bold text-white">{title}</p>
      <p className="mt-1 text-sm leading-relaxed text-slate-400">{body}</p>
    </div>
  );
}

function ChatBubble({
  side,
  who,
  time,
  children,
}: {
  side: "customer" | "ai";
  who: string;
  time: string;
  children: React.ReactNode;
}) {
  const isAi = side === "ai";
  return (
    <div className={"flex " + (isAi ? "justify-end" : "justify-start")}>
      <div
        className={
          "max-w-[85%] rounded-2xl border p-4 text-sm " +
          (isAi
            ? "border-brand-800/80 bg-brand-950/40 text-slate-100"
            : "border-slate-800 bg-slate-900/40 text-slate-200")
        }
      >
        <div className="mb-1.5 flex items-center gap-2 text-xs">
          {isAi ? (
            <>
              <Sparkles className="h-3 w-3 text-brand-400 animate-pulse" />
              <span className="font-bold text-brand-300">{who}</span>
            </>
          ) : (
            <span className="font-bold text-slate-400">{who}</span>
          )}
          <span className="text-slate-650">· {time}</span>
        </div>
        <div className="leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function SystemNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="my-1.5 flex items-start gap-2.5 px-2.5 text-xs text-slate-500">
      <ArrowDown className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-600" />
      <span className="font-mono leading-relaxed">{children}</span>
    </div>
  );
}

function ScreenshotStub({ label, caption }: { label: string; caption: string }) {
  return (
    <div className="mt-3.5 rounded-xl border border-slate-800 bg-slate-900/90 p-4">
      <div className="flex items-center gap-2 text-xs text-slate-400">
        <Monitor className="h-4 w-4" />
        <span className="font-mono">{label}</span>
      </div>
      <div className="mt-2.5 rounded-lg border border-slate-800 bg-slate-950 p-3 text-xs text-slate-350 leading-relaxed">
        {caption}
      </div>
    </div>
  );
}

/* ── Pricing ──────────────────────────────────────────────────────────── */

function Pricing() {
  const tiers = [
    {
      name: "Free trial",
      price: "$0",
      emails: "100 emails · 15 days",
      cta: { label: "Start free trial", href: "/signup", internal: true },
      bullets: [
        "Everything in the product",
        "Bring your own Bedrock model",
        "No credit card required",
        "Self-serve setup",
      ],
    },
    {
      name: "Starter",
      price: "$39",
      cadence: "/mo",
      emails: "1,000 emails / month",
      perEmail: "≈ $0.04 / email",
      highlight: true,
      cta: { label: "Start free trial", href: "/signup", internal: true },
      bullets: [
        "Daily Slack digest",
        "Audit log + reclassify",
        "Custom sender email",
        "Everything in Free Trial"
      ],
    },
    {
      name: "Growth",
      price: "$149",
      cadence: "/mo",
      emails: "5,000 emails / month",
      perEmail: "≈ $0.03 / email",
      cta: { label: "Start free trial", href: "/signup", internal: true },
      bullets: [
        "Everything in Starter",
        "Priority support",
        "Multiple sender domains",
        "PDF + Markdown knowledge base",
      ],
    },
    {
      name: "Enterprise",
      price: "Custom",
      emails: "25,000+ emails",
      cta: { label: "Talk to us", href: "mailto:hello@appgambit.com?subject=Triage%20enterprise%20inquiry" },
      bullets: [
        "Everything in Growth",
        "SSO + audit retention",
        "SLA + named contact",
        "Custom-domain SES setup",
      ],
    },
  ];

  return (
    <section id="pricing" className="py-24 border-t border-slate-200/60 bg-white">
      <div className="mx-auto max-w-6xl px-6">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-extrabold tracking-tight text-slate-950 sm:text-4xl">
            Start free. Scale when it's saving time.
          </h2>
          <p className="mt-4 text-base text-slate-500">
            Predictable per-email pricing. No seat fees. No markup.
          </p>
        </div>
        <div className="mx-auto mt-14 grid max-w-6xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {tiers.map((t) => (
            <div
              key={t.name}
              className={
                "flex flex-col rounded-3xl p-6.5 transition-all duration-300 hover:shadow-lg " +
                (t.highlight
                  ? "border-2 border-brand-500 bg-linear-to-b from-brand-50/50 to-white shadow-md hover:-translate-y-1"
                  : "border border-slate-200 bg-white hover:-translate-y-0.5")
              }
            >
              <p className={`text-xs font-bold uppercase tracking-wider ${t.highlight ? "text-brand-600" : "text-slate-400"}`}>
                {t.name}
              </p>
              <p className="mt-3 flex items-baseline gap-1 text-3xl font-extrabold tracking-tight text-slate-950">
                {t.price}
                {t.cadence && <span className="text-sm font-semibold text-slate-400">{t.cadence}</span>}
              </p>
              <p className="mt-1 text-sm font-semibold text-slate-500">{t.emails}</p>
              {t.perEmail && <p className="text-xs text-slate-400 mt-0.5">{t.perEmail}</p>}
              
              <ul className="mt-7 flex-1 space-y-3">
                {t.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-sm text-slate-650">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              
              {t.cta.internal ? (
                <Link
                  to={t.cta.href}
                  className={
                    "mt-8 inline-flex w-full items-center justify-center gap-1.5 rounded-xl py-3 text-sm font-bold shadow-2xs transition-all active:scale-[0.98] " +
                    (t.highlight
                      ? "bg-slate-950 text-white hover:bg-slate-800 hover:shadow-xs"
                      : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50")
                  }
                >
                  {t.cta.label} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              ) : (
                <a
                  href={t.cta.href}
                  className="mt-8 inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-slate-250 bg-white py-3 text-sm font-bold text-slate-700 hover:bg-slate-50 transition-all active:scale-[0.98]"
                >
                  {t.cta.label}
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Footer ───────────────────────────────────────────────────────────── */

function Footer() {
  return (
    <footer className="border-t border-slate-200/60 bg-white">
      <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-6 px-6 py-12 sm:flex-row sm:items-center">
        <div className="flex items-center gap-2.5">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-950 text-white">
            <Sparkles className="h-3.5 w-3.5" />
          </div>
          <span className="text-sm font-extrabold tracking-tight text-slate-950">Triage</span>
          <span className="text-xs text-slate-400">by APPGAMBiT</span>
        </div>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2.5 text-sm font-semibold text-slate-400">
          <a href="#how" className="hover:text-slate-800">How it works</a>
          <a href="#agent" className="hover:text-slate-800">Agent</a>
          <a href="#features" className="hover:text-slate-800">Features</a>
          <a href="#workflows" className="hover:text-slate-800">Workflows</a>
          <a href="#pricing" className="hover:text-slate-800">Pricing</a>
          <Link to="/signin" className="hover:text-slate-800">Sign in</Link>
          <a href="mailto:hello@appgambit.com" className="hover:text-slate-800">Contact</a>
        </div>
      </div>
      <div className="border-t border-slate-100">
        <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-2 px-6 py-6 text-xs text-slate-400 sm:flex-row sm:items-center font-medium">
          <span>© 2026 <a href="https://appgambit.com" target="_blank" className="hover:underline">APPGAMBiT</a>. AI support inbox operator.</span>
          <span>Built on AWS.</span>
        </div>
      </div>
    </footer>
  );
}
